import React, { forwardRef, useImperativeHandle, useEffect, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import ListKeymap from '@tiptap/extension-list-keymap';
import Link from '@tiptap/extension-link';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import ImageResize from 'tiptap-extension-resize-image';
import Placeholder from '@tiptap/extension-placeholder';
import Highlight from '@tiptap/extension-highlight';
import Youtube from '@tiptap/extension-youtube';
import TextAlign from '@tiptap/extension-text-align'
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import MenuBar from './RichTextEditorMenu/MenuBar';
import './RichTextEditorMenu/RichTextEditor.css';
import { Dialog, DialogContent } from '@mui/material';

// Extend TextStyle to support custom inline styles
const CustomStyle = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      backgroundColor: {
        default: null,
        parseHTML: element => element.style.backgroundColor || null,
        renderHTML: attributes => attributes.backgroundColor ? { style: `background-color: ${attributes.backgroundColor}` } : {},
      },
      borderRadius: {
        default: null,
        parseHTML: element => element.style.borderRadius || null,
        renderHTML: attributes => attributes.borderRadius ? { style: `border-radius: ${attributes.borderRadius}` } : {},
      },
      padding: {
        default: null,
        parseHTML: element => element.style.padding || null,
        renderHTML: attributes => attributes.padding ? { style: `padding: ${attributes.padding}` } : {},
      },
      margin: {
        default: null,
        parseHTML: element => element.style.margin || null,
        renderHTML: attributes => attributes.margin ? { style: `margin: ${attributes.margin}` } : {},
      },
      fontSize: {
        default: null,
        parseHTML: element => element.style.fontSize || null,
        renderHTML: attributes => attributes.fontSize ? { style: `font-size: ${attributes.fontSize}` } : {},
      },
      display: {
        default: null,
        parseHTML: element => element.style.display || null,
        renderHTML: attributes => attributes.display ? { style: `display: ${attributes.display}` } : {},
      },
      color: {
        default: null,
        parseHTML: element => element.style.color || null,
        renderHTML: attributes => attributes.color ? { style: `color: ${attributes.color}` } : {},
      },
    }
  },
})


const RichTextEditor = forwardRef((props, ref) => {
  const { editorStyle, viewMode, value, onChange, onBlur, placeholder, ...restProps } = props;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogImage, setDialogImage] = useState('');

  const extensions = [
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false,
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false,
    },
  }),
  Underline,
  ListKeymap,
  Link.configure({
    protocols: ['ftp', 'mailto'],
    openOnClick: false, 
    autolink: true,
  }),
  Table.configure({
    resizable: true,
    cellMinWidth: 100,
  }),
  TableRow,
  TableCell,
  TableHeader,
  ImageResize.configure({
    inline: true,
    allowBase64: true,
  }),
  Placeholder.configure({
    placeholder: placeholder || 'Enter here...',
  }),
  Highlight,
  Youtube.configure({
    inline: false,
    controls: false,
    nocookie: true,
  }),
  TextAlign.configure({
    types: ['heading', 'paragraph'],
    defaultAlignment: 'left',
  }), 
  TextStyle, 
  Color,
    CustomStyle,
];

  const editor = useEditor({
    extensions,
    content: value,
    editable: viewMode === true ? false : true,
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML());
      }
    },
    editorProps: {
      attributes: restProps,
      handleDOMEvents: {
        click: (view, event) => {
          if (event.target.tagName === 'IMG' && viewMode) {
            setDialogImage(event.target.src);
            setDialogOpen(true);
          }
          return false;
        },
        blur: (view, event) => {
          if (onBlur) {
            onBlur(editor.getHTML());
          }
          return false;
        },
      },
      handleDrop(view, event) {
        event.preventDefault();
        const files = event.dataTransfer.files;
        for (const file of files) {
          if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (e) => {
              const url = e.target.result;
              view.dispatch(
                view.state.tr.replaceSelectionWith(
                  view.state.schema.nodes.image.create({ src: url })
                )
              );
            };
            reader.readAsDataURL(file);
          }
        }
        return true;
      },
      handlePaste(view, event) {
        const items = (event.clipboardData || event.originalEvent.clipboardData).items;
        let handled = false;

        for (const item of items) {
          if (item.kind === 'file') {
            const file = item.getAsFile();
            if (file.type.startsWith('image/')) {
              const reader = new FileReader();
              reader.onload = (e) => {
                const url = e.target.result;
                view.dispatch(
                  view.state.tr.replaceSelectionWith(
                    view.state.schema.nodes.image.create({ src: url })
                  )
                );
              };
              reader.readAsDataURL(file);
              handled = true;
            }
          }
        }

        if (!handled) {
          // If no image was handled, allow the default paste behavior
          setTimeout(() => {
            const pastedContent = event.clipboardData.getData('text/html') || event.clipboardData.getData('text/plain');
            view.dispatch(
              view.state.tr.insertText(pastedContent, view.state.selection.from, view.state.selection.to)
            );
          }, 0);
        }

        return handled;
      },
    },
  });

  const handleCloseDialog = () => {
    setDialogImage('');
    setDialogOpen(false);
  }

  useImperativeHandle(ref, () => ({
    getEditor: () => editor
  }), [editor]);

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      // Only update the editor's content if it differs
      editor.commands.setContent(value);
    }
  }, [editor, value]);

  return (
    <div className="editor" style={(viewMode !== true ? {} : {border: "none"})}>
      {viewMode !== true && <MenuBar editor={editor} />}
      <div style={{ padding: "10px 5px 10px 5px", }}>
        <EditorContent editor={editor} />
      </div>
      <Dialog open={dialogOpen} onClose={handleCloseDialog}
        maxWidth
        sx={{ '& .MuiPaper-root': {width:'85vw'} }}
      >
        <DialogContent>
          <img src={dialogImage} alt="" style={{ width:'100%' }} />
        </DialogContent>
      </Dialog>
    </div>
  )
});

export default RichTextEditor;